/**
 * MgpPoSChart component module.
 * @module shared/widgets/MgpPoSChart
 */
import React from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more.js';
import solidGauge from 'highcharts/modules/solid-gauge.js';
import noDataToDisplay from 'highcharts/modules/no-data-to-display.js';
import styled from 'styled-components/macro';

highchartsMore(Highcharts);
solidGauge(Highcharts);
noDataToDisplay(Highcharts);

const _Wrapper = styled.div`
  height: ${props => props.height}px;
  width: ${props => props.width}px;

  .highcharts-container {
    height: ${props => props.height}px !important;
    width: ${props => props.width}px !important;
  }
`;

const breakpoints = {
  md: {
    height: 200,
    width: 200,
    size: '100%',
    center: ['50%', '60%'],
    centerLabel: {
      position: -17,
      numberFontSize: '20px',
      posLabelSize: '10px',
      textFontSize: '12px',
    },
    arrowWidth: 30,
    noData: {
      numberPosition: 8,
      labelPosition: -30,
    },
  },
  sm: {
    height: 130,
    width: 130,
    size: '100%',
    center: ['50%', '60%'],
    centerLabel: {
      position: -17,
      numberFontSize: '20px',
      posLabelSize: '10px',
      textFontSize: '12px',
    },
    arrowWidth: 20,
    noData: {
      numberPosition: 8,
      labelPosition: -7,
    },
  },
  xs: {
    height: 40,
    width: 75,
    size: '170%',
    center: ['50%', '90%'],
    centerLabel: {
      position: -14,
      numberFontSize: '12px',
      posLabelSize: '10px',
      textFontSize: '12px',
    },
    arrowWidth: 10,
    noData: {
      numberPosition: 11,
      labelPosition: null,
    },
  },
};
/**
 * MgpPoSChart component
 * @name MgpPoSChart
 */
const MgpPoSChart = (props) => {
  const {probabilityOfSuccess, layoutSize = 'sm'} = props;
  const {percent, text} = probabilityOfSuccess;
  const posNumber = percent ? Number(percent.substring(0, percent.length - 1)) : null;
  let arrowPosition;

  if (text) {
    if (text === 'Below Confidence Zone') {
      switch (true) {
        case (posNumber >= 66):
          arrowPosition = 65;
          break;
        default:
          arrowPosition = Math.floor(posNumber / 5) * 5;
      }
    }
    if (text === 'Above Confidence Zone') {
      arrowPosition = 95;
    }
    if (text === 'In Confidence Zone') {
      arrowPosition = 80;
    }
  }

  const renderLabel = (_layoutSize) => {
    const posLabel = `<span style="font-size: ${breakpoints[_layoutSize].centerLabel.posLabelSize}; font-family: ProximaRegular; color: white;">Probability of Success</span><br/>`;
    const textLabel = `<div style="font-size: ${breakpoints[_layoutSize].centerLabel.textFontSize}; font-family: ProximaRegular; color: white; padding-top: 5px;">${text}</div>`;

    return `<div style="text-align:center">
      <span style="font-size: ${breakpoints[_layoutSize].centerLabel.numberFontSize}; font-family: ProximaRegular; color: white;">${posNumber}%</span><br/>
      ${layoutSize !== 'xs' ? posLabel : ''}
      ${layoutSize !== 'xs' ? textLabel : ''}
    </div>`;
  };

  return <_Wrapper width={breakpoints[layoutSize].width} height={breakpoints[layoutSize].height}>
    <HighchartsReact
      containerProps={{
        style: {width: '100%', height: '100%'},
      }}
      highcharts={Highcharts}
      options={{
        chart: {
          type: 'solidgauge',
          backgroundColor: 'rgba(255,255,255,0)',
          animation: false,
          height: breakpoints[layoutSize].height,
          plotBackgroundColor: null,
          plotBackgroundImage: null,
          plotBorderWidth: 0,
          plotShadow: false,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0],
        },
        tooltip: {
          enabled: false,
        },
        pane: {
          size: breakpoints[layoutSize].size,
          center: breakpoints[layoutSize].center,
          startAngle: -95,
          endAngle: 95,
          background: {
            innerRadius: '60%',
            outerRadius: '100%',
            shape: 'arc',
            backgroundColor: 'rgba(0,0,255,0)',
            borderColor: 'rgba(0,255,0,0)',
          },
        },
        yAxis: {
          lineWidth: 0,
          tickWidth: 0,
          minorTickInterval: null,
          tickAmount: 2,
          labels: {
            enabled: false,
          },
          min: 0,
          max: 100,
          plotBands: [{
            from: 0,
            to: 70,
            thickness: '40%',
            color: '#64399c',
          }, {
            from: 70,
            to: 90,
            thickness: '40%',
            color: '#60bd44',
          }, {
            from: 90,
            to: 100,
            thickness: '40%',
            color: '#327bc5',
          }],
        },
        plotOptions: {
          series: {
            clip: false,
          },
          gauge: {
            dataLabels: {
              y: breakpoints[layoutSize].centerLabel.position,
              borderWidth: 0,
              useHTML: true,
            },
            dial: {
              radius: '90%',
              backgroundColor: '#FFFFFF',
              borderColor: '#777777',
              borderWidth: 1,
              baseWidth: 1,
              topWidth: breakpoints[layoutSize].arrowWidth,
              baseLength: '70%',
              rearLength: '-70%',
            },
            pivot: {
              radius: 0,
            },
          },
        },
        credits: {
          enabled: false,
        },
        title: {
          // eslint-disable-next-line no-nested-ternary
          text: layoutSize !== 'xs'
            ? typeof arrowPosition === 'number' ? null : text
            : null,
          verticalAlign: 'bottom',
          style: {
            color: 'white',
            fontSize: breakpoints[layoutSize].centerLabel.textFontSize,
            fontFamily: 'ProximaRegular',
            fontWeight: 'bold',
          },
          y: breakpoints[layoutSize].noData.labelPosition,
          widthAdjust: 0,
        },
        series: [{
          name: 'POS',
          type: 'gauge',
          data: [arrowPosition],
          dataLabels: {
            format: renderLabel(layoutSize),
            crop: false,
            overflow: 'allow',
          },
        }],
        lang: {
          noData: percent,
        },
        noData: {
          style: {
            color: 'white',
            fontSize: breakpoints[layoutSize].centerLabel.numberFontSize,
            fontFamily: 'ProximaRegular',
          },
          position: {
            y: breakpoints[layoutSize].noData.numberPosition,
          },
        },
      }}
    />
  </_Wrapper>;
};

export default MgpPoSChart;

MgpPoSChart.propTypes = {
  probabilityOfSuccess: PropTypes.number,
  /**
   * The size of the component ('xs', 'sm', or 'md')
   */
  layoutSize: PropTypes.string,
};
